/**
 * The live view of a Standard run (DESIGN UD-20 adapted to bench v3, ED-86): what the parallel scheduler is doing behind the
 * status line. Pure state transitions, no DOM and no clock of their own (the caller passes `now`), so they are tested in Node.
 *
 * A v3 run works in rounds ("waves", BENCH-V3-PLAN §3): every request of round w goes out, the answers are applied in text
 * order, then round w + 1. Stretches therefore finish in bursts at a round's end, and "N of M stretches" stands still for a
 * whole round. The worker's `progress` message (after every applied answer) carries the round, the answers in, the requests in
 * flight and what the last applied answer did; once a second it also carries the list of stretches (`ChunkView`). The
 * limiter's falls in `limit` are the slowdowns. This module folds those into one {@link RunLive} record and a bounded list
 * of events (the last {@link MAX_EVENTS}, newest first).
 */
import type { ChunkStatus, ChunkView, SchedulerProgress } from '../../engine/bench/loop2.ts';
import type { ChunkBrief } from '../../shared/bench-protocol.ts';
import { WORKING } from '../strings.ts';

export const MAX_EVENTS = 50;
/** The stretches table shows at most this many rows (TEST-PLAN §9: 700 stretches must not cost a long main-thread task). */
export const MAX_TABLE_ROWS = 50;
/** Rounds a stretch can take at most (`maxAttempts + 1`, Economy's `MAX_ROUNDS`). */
export const MAX_ROUNDS = 4;

export type RunEventData =
	| { kind: 'started'; total: number }
	| { kind: 'resumed'; done: number; total: number }
	| { kind: 'wave'; wave: number; requests: number }
	| {
			kind: 'answer';
			a: number;
			b: number;
			round: number;
			took: boolean;
			accepted: number;
			rejected: number;
			reason?: string;
	  }
	| { kind: 'finished'; a: number; b: number; status: ChunkStatus }
	| { kind: 'slowdown'; limit: number }
	| { kind: 'brief' };

export interface RunEvent {
	/** Epoch ms. */
	at: number;
	data: RunEventData;
}

export interface RunLive {
	jobId: string;
	phase: SchedulerProgress['phase'];
	/** 0 until the first progress message of the run. */
	wave: number;
	waveDone: number;
	wavePending: number;
	inFlight: number;
	flyingKeys: readonly string[];
	superseded: number;
	skipped: number;
	doneChunks: number;
	totalChunks: number;
	fraction: number;
	/** The latest list of stretches (at most a second old). */
	chunks: readonly ChunkView[];
	/** Stretch key to the sequence number of its last change: the table lists the newest changes first. */
	changed: ReadonlyMap<string, number>;
	seq: number;
	slowdowns: number;
	/** Epoch ms of the last slowdown. */
	lastSlowdownAt: number | null;
	events: readonly RunEvent[];
	/** The step of the final passes, in a `final` phase (the audit's F-1.8). */
	final?: SchedulerProgress['final'];
}

const withEvent = (events: readonly RunEvent[], at: number, data: RunEventData): RunEvent[] =>
	[{ at, data }, ...events].slice(0, MAX_EVENTS);

/** A run that has just started (or resumed): no progress yet. */
export function startLive(
	jobId: string,
	done: number,
	total: number,
	now: number,
	resumed = false
): RunLive {
	return {
		jobId,
		phase: 'main',
		wave: 0,
		waveDone: 0,
		wavePending: 0,
		inFlight: 0,
		flyingKeys: [],
		superseded: 0,
		skipped: 0,
		doneChunks: done,
		totalChunks: total,
		fraction: total > 0 ? done / total : 0,
		chunks: [],
		changed: new Map(),
		seq: 0,
		slowdowns: 0,
		lastSlowdownAt: null,
		events: withEvent(
			[],
			now,
			resumed || done > 0 ? { kind: 'resumed', done, total } : { kind: 'started', total }
		),
	};
}

/** Folds a worker `progress` message (and its stretch list, when it carries one) into the record. */
export function applyProgress(
	prev: RunLive,
	p: SchedulerProgress,
	chunks: readonly ChunkView[] | undefined,
	now: number
): RunLive {
	let events = prev.events;
	let seq = prev.seq;
	const changed = new Map(prev.changed);
	// a new round (or the first message of the run): its size is what is in and what is still to come; the final passes
	// start no round
	if (p.phase !== 'final' && (p.wave !== prev.wave || p.phase !== prev.phase))
		events = withEvent(events, now, {
			kind: 'wave',
			wave: p.wave,
			requests: p.waveDone + p.wavePending,
		});
	const a = p.lastApplied;
	if (a) {
		events = withEvent(
			events,
			now,
			a.took
				? {
						kind: 'answer',
						a: a.a,
						b: a.b,
						round: a.round,
						took: true,
						accepted: a.accepted,
						rejected: a.rejected,
						...(a.reason ? { reason: a.reason } : {}),
					}
				: { kind: 'answer', a: a.a, b: a.b, round: a.round, took: false, accepted: 0, rejected: 0 }
		);
		changed.set(a.key, ++seq);
	}
	if (chunks) {
		const before = new Map(prev.chunks.map((c) => [c.key, c]));
		for (const c of chunks) {
			const o = before.get(c.key);
			if (
				o &&
				c.key !== a?.key &&
				(o.rounds !== c.rounds || o.done !== c.done || o.open !== c.open)
			)
				changed.set(c.key, ++seq);
		}
	}
	return {
		...prev,
		phase: p.phase,
		wave: p.wave,
		waveDone: p.waveDone,
		wavePending: p.wavePending,
		inFlight: p.inFlight,
		flyingKeys: p.flyingKeys,
		superseded: p.superseded,
		skipped: p.skipped,
		doneChunks: p.doneChunks,
		totalChunks: p.totalChunks,
		fraction: p.fraction,
		chunks: chunks ?? prev.chunks,
		changed,
		seq,
		events,
		...(p.final ? { final: p.final } : {}),
	};
}

/** A stretch the worker just finished (the chunk message's `last`). */
export function noteFinished(prev: RunLive, last: ChunkBrief, now: number): RunLive {
	const changed = new Map(prev.changed);
	const seq = prev.seq + 1;
	changed.set(last.key, seq);
	return {
		...prev,
		changed,
		seq,
		events: withEvent(prev.events, now, {
			kind: 'finished',
			a: last.a,
			b: last.b,
			status: last.status,
		}),
	};
}

/** A fall in the limiter's limit: the service asked for a slower pace (or the output budget capped it). */
export function noteSlowdown(prev: RunLive, limit: number, now: number): RunLive {
	return {
		...prev,
		slowdowns: prev.slowdowns + 1,
		lastSlowdownAt: now,
		events: withEvent(prev.events, now, { kind: 'slowdown', limit }),
	};
}

/** A one-off event (the document brief was made). */
export function noteEvent(prev: RunLive, data: RunEventData, now: number): RunLive {
	return { ...prev, events: withEvent(prev.events, now, data) };
}

/**
 * The status line of the parallel path (replaces "N of M stretches", which stands still for a whole round): "Round 2: 3 of 5
 * answers in · 4 requests in flight". `flying` is the limiter's count when there is one (it is live between the worker's
 * messages), else the scheduler's.
 */
export function roundLine(live: RunLive | null, flying: number | null): string {
	const R = WORKING.round;
	if (live?.phase === 'final') return finalLine(live.final);
	if (!live || live.wave === 0) {
		const n = flying ?? live?.inFlight ?? 0;
		return n > 0 ? `${R.starting} · ${R.inFlight(String(n))}` : R.starting;
	}
	const total = live.waveDone + live.wavePending;
	const head =
		live.phase === 'top-up'
			? R.finalPass(String(live.waveDone), String(total))
			: R.answers(String(live.wave), String(live.waveDone), String(total));
	return `${head} · ${R.inFlight(String(flying ?? live.inFlight))}`;
}

/** The status line of the final passes (the audit's F-1.8): the step and, when it has one, its count. */
export function finalLine(f: SchedulerProgress['final']): string {
	const F = WORKING.round.finalChecks;
	if (!f || f.step === 'checkpoint') return F.checkpoint;
	if (f.step === 'analysis') return F.analysis(String(f.done), String(f.total));
	return F.lenient(String(f.done), String(f.total));
}

/** One row of the stretches table, with how to describe its state. */
export interface TableRow {
	view: ChunkView;
	state: 'flying' | 'applied' | 'waiting' | 'done';
}

/**
 * The rows to show: stretches with a request in flight first, then the latest changes (answers applied, stretches finished),
 * then those still waiting in text order; at most {@link MAX_TABLE_ROWS}. `counts` covers the whole list.
 */
export function tableRows(live: RunLive): {
	rows: TableRow[];
	total: number;
	flying: number;
	waiting: number;
	finished: number;
} {
	const fly = new Set(live.flyingKeys);
	const flying: TableRow[] = [];
	const touched: TableRow[] = [];
	const waiting: TableRow[] = [];
	let finished = 0;
	for (const v of live.chunks) {
		if (v.done !== undefined) {
			finished++;
			touched.push({ view: v, state: 'done' });
		} else if (fly.has(v.key)) flying.push({ view: v, state: 'flying' });
		else if (v.rounds > 0) touched.push({ view: v, state: 'applied' });
		else waiting.push({ view: v, state: 'waiting' });
	}
	const at = (r: TableRow): number => live.changed.get(r.view.key) ?? 0;
	touched.sort((x, y) => at(y) - at(x) || x.view.a - y.view.a);
	const rows = [...flying, ...touched, ...waiting].slice(0, MAX_TABLE_ROWS);
	return {
		rows,
		total: live.chunks.length,
		flying: flying.length,
		waiting: waiting.length + touched.filter((r) => r.state === 'applied').length,
		finished,
	};
}
